import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { BRANDING } from '../../config/branding';
import { SPORT_ICON_MAP, SPORTS } from '../../utils/constants';
import { getProps } from '../../api/props';
import { getPlayerData } from '../../api/players';
import { getUpcomingGames } from '../../api/games';
import { attachTeamDetails } from '../../components/GameCard/utils';
import { getAllTeamData } from '../../api/teams';
import {
  FilterRow,
  GameBody,
  GameGroupHeader,
  GameGroupWrap,
  GameMatchup,
  GameTime,
  HitBadge,
  HistoryTable,
  Inner,
  MetaChip,
  ModalClose,
  ModalMeta,
  ModalOverlay,
  ModalPlayerName,
  ModalSheet,
  MobileTabSelect,
  PageWrap,
  PositionBadge,
  PropCard,
  PropCardFooter,
  PropCardTop,
  PropLineRow,
  PropLineValue,
  PropPlayerName,
  PropStatPill,
  PropTab,
  PropTabs,
  PropTeamBadge,
  PropTypeName,
  PropTypeTab,
  PropTypeTabs,
  PropsEyebrow,
  PropsHeader,
  PropsTitle,
  ResultCount,
  SearchInput,
  SkeletonBlock,
  SportTab,
  SportTabs,
  StatBox,
  StatSummaryRow,
  StateWrap,
  TeamGroupCount,
  TeamGroupGrid,
  TeamGroupHeader,
  TeamGroupName,
  TeamGroupWrap,
  TeamLogoCircle,
  ViewHistoryChip,
} from './styles';

// ─── Player detail modal ─────────────────────────────────────────────────────

// Extract the actual stat value from a game log entry for a given prop
const getActualFromLog = (log, prop) => {
  const stats = prop.connectedStats || (prop.connectedStat ? [prop.connectedStat] : []);
  if (!stats.length) return null;
  return stats.reduce((sum, statId) => {
    const entry = log[statId];
    // game log stats are either { value: N } objects or raw numbers
    const v = typeof entry === 'object' ? (entry?.value ?? 0) : (Number(entry) || 0);
    return sum + v;
  }, 0);
};

const PlayerModal = ({ sport, player, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activePropId, setActivePropId] = useState(null);
  const [historyPage, setHistoryPage] = useState(0);
  const [filterByOpponent, setFilterByOpponent] = useState(false);

  const PAGE_SIZE = 5;

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getPlayerData(sport, player.playerId || player.id);
        setData(res);
        setActivePropId(res?.props?.[0]?.id ?? null);
      } catch (err) {
        setError(err.message || 'Failed to load player data.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [sport, player]);

  const handleOverlayClick = e => {
    if (e.target === e.currentTarget) onClose();
  };

  const selectedProp = useMemo(
    () => (data?.props || []).find(p => p.id === activePropId),
    [data, activePropId]
  );

  // Reset page + opponent filter when prop tab changes
  useEffect(() => { setHistoryPage(0); setFilterByOpponent(false); }, [activePropId]);

  // Derive game-by-game history from gameLogs using the prop's connected stats
  const propHistory = useMemo(() => {
    if (!selectedProp || !Array.isArray(data?.gameLogs)) return [];
    return data.gameLogs.map(log => {
      const actual = getActualFromLog(log, selectedProp);
      const hit = actual != null ? actual > selectedProp.value : null;
      return {
        date: log.date,
        opponent: log.opponent,
        opponentId: log.opponentId,
        gameResult: log.result,
        actual,
        line: selectedProp.value,
        hit,
      };
    });
  }, [data, selectedProp]);

  // Derive opponent info from the game stored on the clicked player
  const { opponentId, opponentAbbr } = useMemo(() => {
    const game = player._game;
    const playerTeamId = data?.teamId;
    if (!game || !playerTeamId) return { opponentId: null, opponentAbbr: null };
    const isAway = String(playerTeamId) === String(game.awayTeam?.teamId);
    const oppTeam = isAway ? game.homeTeam : game.awayTeam;
    const abbr = oppTeam?.teamAbbr || oppTeam?.abbr ||
      String(oppTeam?.teamName || '').slice(0, 3).toUpperCase() || null;
    return { opponentId: oppTeam?.teamId ?? null, opponentAbbr: abbr };
  }, [player._game, data?.teamId]);

  // Apply opponent filter
  const filteredHistory = useMemo(() => {
    if (!filterByOpponent || !opponentId) return propHistory;
    return propHistory.filter(r => String(r.opponentId) === String(opponentId));
  }, [propHistory, filterByOpponent, opponentId]);

  const hitCount = filteredHistory.filter(r => r.hit === true).length;
  const hitRate = filteredHistory.length ? Math.round((hitCount / filteredHistory.length) * 100) : null;
  const avg = filteredHistory.length
    ? (filteredHistory.reduce((s, r) => s + (r.actual ?? 0), 0) / filteredHistory.length).toFixed(2)
    : null;

  const totalPages = Math.ceil(filteredHistory.length / PAGE_SIZE);
  const pagedHistory = filteredHistory.slice(historyPage * PAGE_SIZE, (historyPage + 1) * PAGE_SIZE);

  // Season stats from data.stats (object keyed by stat id)
  const seasonStats = useMemo(() => {
    if (!data?.stats) return [];
    return Object.values(data.stats);
  }, [data]);

  const formatOdds = n => {
    const v = Number(n);
    return Number.isNaN(v) ? '—' : (v >= 0 ? `+${v}` : `${v}`);
  };

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalSheet>
        <ModalClose onClick={onClose} aria-label="Close">
          <i className="fas fa-times" />
        </ModalClose>

        <ModalPlayerName>{data?.name || player.playerName || player.name || 'Player'}</ModalPlayerName>
        <ModalMeta>
          {(player.teamName || player.team) && <MetaChip>{player.teamName || player.team}</MetaChip>}
          {(data?.position || player.position) && <MetaChip>{data?.position || player.position}</MetaChip>}
          <MetaChip $accent>{String(sport).toUpperCase()} Props</MetaChip>
        </ModalMeta>

        {loading && (
          <div style={{ display: 'grid', gap: '0.65rem' }}>
            <SkeletonBlock $h="2.5rem" />
            <SkeletonBlock $h="10rem" />
          </div>
        )}

        {!loading && error && (
          <StateWrap>
            <i className="fas fa-exclamation-triangle" />
            <h4>Could Not Load Player</h4>
            <p>{error}</p>
          </StateWrap>
        )}

        {!loading && !error && data && (
          <>
            {/* Season stats strip */}
            {seasonStats.length > 0 && (
              <div style={{
                display: 'flex', flexWrap: 'wrap', gap: '0.4rem',
                marginBottom: '0.85rem', paddingBottom: '0.7rem',
                borderBottom: '1px solid rgba(255,255,255,0.07)',
              }}>
                {seasonStats.map(stat => (
                  <div key={stat.id} style={{
                    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 8, padding: '3px 10px', fontSize: '0.72rem',
                  }}>
                    <span style={{ color: '#64748b', marginRight: 4 }}>{stat.label}</span>
                    <span style={{ fontWeight: 700, color: '#f8fafc' }}>{stat.value}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Prop type tabs */}
            {(data.props || []).length > 0 && (
              <PropTabs>
                {data.props.map(p => (
                  <PropTab
                    key={p.id}
                    $active={activePropId === p.id}
                    onClick={() => setActivePropId(p.id)}
                  >
                    {p.name || p.label || p.id}
                  </PropTab>
                ))}
              </PropTabs>
            )}

            {selectedProp && (
              <>
                <StatSummaryRow>
                  <StatBox>
                    <div className="label">Line</div>
                    <div className="value">O/U {selectedProp.value}</div>
                  </StatBox>
                  <StatBox>
                    <div className="label">Over</div>
                    <div className="value" style={{ color: '#60a5fa' }}>{formatOdds(selectedProp.overOdds)}</div>
                  </StatBox>
                  <StatBox>
                    <div className="label">Under</div>
                    <div className="value" style={{ color: '#94a3b8' }}>{formatOdds(selectedProp.underOdds)}</div>
                  </StatBox>
                  <StatBox>
                    <div className="label">{filterByOpponent ? `vs ${opponentAbbr}` : `L${filteredHistory.length}`} Hit%</div>
                    <div className="value" style={{ color: hitRate != null && hitRate >= 60 ? '#4ade80' : hitRate != null && hitRate <= 40 ? '#f87171' : '#f8fafc' }}>
                      {hitRate != null ? `${hitRate}%` : '—'}
                    </div>
                  </StatBox>
                  <StatBox>
                    <div className="label">Avg</div>
                    <div className="value">{avg ?? '—'}</div>
                  </StatBox>
                </StatSummaryRow>

                {/* Opponent filter toggle */}
                {opponentAbbr && (
                  <div style={{ marginBottom: '0.65rem', display: 'flex', gap: '0.45rem', alignItems: 'center' }}>
                    <button
                      onClick={() => { setFilterByOpponent(v => !v); setHistoryPage(0); }}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                        padding: '0.32rem 0.8rem',
                        borderRadius: 999,
                        border: `1px solid ${filterByOpponent ? 'rgba(74,222,128,0.55)' : 'rgba(148,163,184,0.28)'}`,
                        background: filterByOpponent ? 'rgba(22,163,74,0.16)' : 'rgba(255,255,255,0.05)',
                        color: filterByOpponent ? '#4ade80' : '#94a3b8',
                        fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer',
                        letterSpacing: '0.03em',
                      }}
                    >
                      <i className="fas fa-filter" style={{ fontSize: '0.65rem' }} />
                      vs {opponentAbbr}
                    </button>
                    {filterByOpponent && (
                      <span style={{ color: '#64748b', fontSize: '0.72rem' }}>
                        {filteredHistory.length} games vs {opponentAbbr}
                      </span>
                    )}
                  </div>
                )}

                {propHistory.length > 0 ? (
                  <>
                  <HistoryTable>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Opp</th>
                        <th>Score</th>
                        <th>Actual</th>
                        <th>Line</th>
                        <th>Result</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedHistory.map((r, i) => (
                        <tr key={i}>
                          <td>{r.date || '—'}</td>
                          <td style={{ fontWeight: 600 }}>{r.opponent || '—'}</td>
                          <td style={{ color: r.gameResult?.startsWith('W') ? '#4ade80' : '#f87171', fontWeight: 700, fontSize: '0.78rem' }}>
                            {r.gameResult || '—'}
                          </td>
                          <td style={{ fontWeight: 800, color: '#f8fafc', fontSize: '1rem' }}>
                            {r.actual ?? '—'}
                          </td>
                          <td>{r.line}</td>
                          <td>
                            <HitBadge $hit={r.hit === true ? true : r.hit === false ? false : undefined}>
                              {r.hit === true ? <><i className="fas fa-check" /> Over</> :
                               r.hit === false ? <><i className="fas fa-times" /> Under</> : '—'}
                            </HitBadge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </HistoryTable>
                  {totalPages > 1 && (
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '0.6rem 0.2rem 0',
                      borderTop: '1px solid rgba(255,255,255,0.06)',
                      marginTop: '0.25rem',
                    }}>
                      <button
                        onClick={() => setHistoryPage(p => Math.max(0, p - 1))}
                        disabled={historyPage === 0}
                        style={{
                          background: historyPage === 0 ? 'transparent' : 'rgba(255,255,255,0.07)',
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: 8, color: historyPage === 0 ? '#334155' : '#e2e8f0',
                          padding: '0.35rem 0.8rem', cursor: historyPage === 0 ? 'default' : 'pointer',
                          fontSize: '0.8rem', fontWeight: 700,
                        }}
                      >
                        <i className="fas fa-chevron-left" style={{ marginRight: 5 }} />Prev
                      </button>
                      <span style={{ color: '#64748b', fontSize: '0.78rem', fontWeight: 600 }}>
                        {historyPage + 1} / {totalPages}
                        <span style={{ color: '#475569', marginLeft: 6 }}>({filteredHistory.length} games{filterByOpponent ? ` vs ${opponentAbbr}` : ''})</span>
                      </span>
                      <button
                        onClick={() => setHistoryPage(p => Math.min(totalPages - 1, p + 1))}
                        disabled={historyPage >= totalPages - 1}
                        style={{
                          background: historyPage >= totalPages - 1 ? 'transparent' : 'rgba(255,255,255,0.07)',
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: 8, color: historyPage >= totalPages - 1 ? '#334155' : '#e2e8f0',
                          padding: '0.35rem 0.8rem', cursor: historyPage >= totalPages - 1 ? 'default' : 'pointer',
                          fontSize: '0.8rem', fontWeight: 700,
                        }}
                      >
                        Next<i className="fas fa-chevron-right" style={{ marginLeft: 5 }} />
                      </button>
                    </div>
                  )}
                  </>
                ) : (
                  <StateWrap style={{ padding: '2rem 0' }}>
                    <i className="fas fa-history" style={{ fontSize: '1.8rem' }} />
                    <p>{filterByOpponent
                      ? `No game log entries found vs ${opponentAbbr || 'this opponent'} yet.`
                      : 'No game log history available for this player yet.'}</p>
                  </StateWrap>
                )}
              </>
            )}

            {!selectedProp && (
              <StateWrap style={{ padding: '2rem 0' }}>
                <i className="fas fa-layer-group" />
                <p>No prop data available for this player.</p>
              </StateWrap>
            )}
          </>
        )}
      </ModalSheet>
    </ModalOverlay>
  );
};

// ─── Main Props page ─────────────────────────────────────────────────────────

// Parse { playerProps: { [id]: { name, players } } } into groups
const parsePropsGroups = raw => {
  const groups = [];
  for (const [id, group] of Object.entries(raw?.playerProps || {})) {
    const players = Array.isArray(group?.players)
      ? group.players
      : Object.values(group?.players || {});
    if (players.length) {
      groups.push({ id, name: group?.name || group?.label || id, players });
    }
  }
  return groups;
};

// Group players by game (using teamId matching), then by team within each game
const groupByGame = (players, games) => {
  const gameGroups = [];
  const matchedSet = new Set();

  games.forEach(game => {
    const awayId = String(game.awayTeam?.teamId ?? '');
    const homeId = String(game.homeTeam?.teamId ?? '');
    const awayPlayers = players.filter(p => String(p.teamId ?? '') === awayId);
    const homePlayers = players.filter(p => String(p.teamId ?? '') === homeId);
    if (awayPlayers.length || homePlayers.length) {
      awayPlayers.forEach(p => matchedSet.add(p));
      homePlayers.forEach(p => matchedSet.add(p));
      gameGroups.push({ game, awayPlayers, homePlayers });
    }
  });

  // Players not matched to any game — group by team as fallback
  const unmatched = players.filter(p => !matchedSet.has(p));
  const unmatchedByTeam = new Map();
  unmatched.forEach(p => {
    const t = p.teamName || p.team || 'Unknown';
    if (!unmatchedByTeam.has(t)) unmatchedByTeam.set(t, []);
    unmatchedByTeam.get(t).push(p);
  });

  return { gameGroups, unmatchedByTeam };
};

const PropsPage = () => {
  const { sport: paramSport } = useParams();
  const history = useHistory();
  const hasPropsAccess = useSelector(state => Boolean(state.access.paid) && Boolean(state.access.hasPropsUpgrade));

  const [sport, setSport] = useState(paramSport || 'mlb');
  const [rawData, setRawData] = useState(null);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [activePropTypeId, setActivePropTypeId] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  // Keep local sport state in sync with URL param.
  useEffect(() => {
    const target = paramSport || 'mlb';
    if (SPORTS.includes(target) && target !== sport) {
      setSport(target);
    }
  }, [paramSport, sport]);

  const changeSport = useCallback(s => {
    setSport(s);
    setSearch('');
    setActivePropTypeId(null);
    history.replace(`/props/${s}`);
  }, [history]);

  useEffect(() => {
    if (!hasPropsAccess) {
      setLoading(false);
      setError('');
      setRawData(null);
      setGames([]);
      setActivePropTypeId(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError('');
    setRawData(null);
    setGames([]);
    setActivePropTypeId(null);

    const load = async () => {
      try {
        const [propsData, gamesData, teamsData] = await Promise.all([
          getProps(sport),
          getUpcomingGames(sport).catch(() => []),
          getAllTeamData(sport).catch(() => []),
        ]);
        if (!cancelled) {
          const teamList = Array.isArray(teamsData) ? teamsData : teamsData?.data || [];
          const rawGames = Array.isArray(gamesData) ? gamesData : gamesData?.data || [];
          setRawData(propsData);
          setGames(attachTeamDetails(rawGames, teamList));
          const firstId = Object.keys(propsData?.playerProps || {})[0] || null;
          setActivePropTypeId(firstId);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load props data.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [sport, hasPropsAccess]);

  const allGroups = useMemo(() => parsePropsGroups(rawData), [rawData]);

  // Active prop type group filtered by search
  const activeGroup = useMemo(() => {
    const group = allGroups.find(g => g.id === activePropTypeId) || allGroups[0];
    if (!group) return null;
    if (!search.trim()) return group;
    const q = search.trim().toLowerCase();
    return {
      ...group,
      players: group.players.filter(p =>
        (p.playerName || p.name || '').toLowerCase().includes(q) ||
        (p.teamName || p.team || '').toLowerCase().includes(q)
      ),
    };
  }, [allGroups, activePropTypeId, search]);

  // Organize active prop's players by game then team
  const { gameGroups, unmatchedByTeam } = useMemo(
    () => activeGroup ? groupByGame(activeGroup.players, games) : { gameGroups: [], unmatchedByTeam: new Map() },
    [activeGroup, games]
  );

  const sortedSports = SPORTS;

  const formatOdds = n => {
    if (n == null) return null;
    const v = Number(n);
    return Number.isNaN(v) ? null : (v >= 0 ? `+${v}` : `${v}`);
  };

  const renderPlayerCard = (p, game) => {
    const name = p.playerName || p.name || 'Unknown';
    const team = p.teamAbbr || p.team || '';
    const line = p.value ?? p.line ?? p.propValue;
    const overOdds = formatOdds(p.overOdds);
    const underOdds = formatOdds(p.underOdds);

    return (
      <PropCard key={`${p.playerId || p.id || name}-${line}`} onClick={() => setSelectedPlayer({ ...p, _groupName: activeGroup?.name, _game: game || null })}>
        <PropCardTop>
          <PropPlayerName>{name}</PropPlayerName>
          {team && <PropTeamBadge>{team}</PropTeamBadge>}
          {p.position && <PositionBadge>{p.position}</PositionBadge>}
        </PropCardTop>
        <PropLineRow>
          <PropTypeName>{activeGroup?.name}</PropTypeName>
          <PropLineValue>{line != null ? `O/U ${line}` : '—'}</PropLineValue>
        </PropLineRow>
        <PropCardFooter>
          {overOdds && <PropStatPill style={{ color: '#4ade80' }}>O {overOdds}</PropStatPill>}
          {underOdds && <PropStatPill style={{ color: '#f87171' }}>U {underOdds}</PropStatPill>}
          <ViewHistoryChip>
            View History <i className="fas fa-chevron-right" style={{ fontSize: '0.6rem' }} />
          </ViewHistoryChip>
        </PropCardFooter>
      </PropCard>
    );
  };

  return (
    <PageWrap>
      <Helmet>
        <title>{`${String(sport).toUpperCase()} Props | ${BRANDING.appName}`}</title>
        <meta name="description" content={`Live player props with history for ${String(sport).toUpperCase()}.`} />
      </Helmet>

      <Inner>
        <PropsHeader>
          <div>
            <PropsTitle>
              <i className={`fas ${SPORT_ICON_MAP[sport] || 'fa-trophy'}`} style={{ marginRight: '0.5rem', color: '#f0c876' }} />
              Player Props
            </PropsTitle>
            <PropsEyebrow>Click any player to view their prop history</PropsEyebrow>
          </div>
          <SportTabs>
            {sortedSports.map(s => (
              <SportTab
                key={s}
                $active={sport === s}
                onClick={() => changeSport(s)}
                title={s.toUpperCase()}
              >
                <i className={`fas ${SPORT_ICON_MAP[s]}`} style={{ marginRight: 4 }} />
                {s.toUpperCase()}
              </SportTab>
            ))}
          </SportTabs>
        </PropsHeader>

        {/* Prop type tabs — desktop horizontal scroll, mobile dropdown */}
        {!loading && allGroups.length > 0 && (
          <>
            <PropTypeTabs>
              {allGroups.map(g => (
                <PropTypeTab
                  key={g.id}
                  $active={activePropTypeId === g.id}
                  onClick={() => { setActivePropTypeId(g.id); setSearch(''); }}
                >
                  {g.name}
                </PropTypeTab>
              ))}
            </PropTypeTabs>
            <MobileTabSelect
              value={activePropTypeId || ''}
              onChange={e => { setActivePropTypeId(e.target.value); setSearch(''); }}
            >
              {allGroups.map(g => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </MobileTabSelect>
          </>
        )}

        <FilterRow>
          <SearchInput
            placeholder="Search player or team..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {!loading && activeGroup && (
            <ResultCount>{activeGroup.players.length} players</ResultCount>
          )}
        </FilterRow>

        {!hasPropsAccess && (
          <StateWrap>
            <i className="fas fa-lock" />
            <h4>Props Add-On Required</h4>
            <p>Activate the Props add-on to access player props and history.</p>
            <button
              type="button"
              onClick={() => history.push('/settings?tab=subscription-management')}
              style={{
                border: '1px solid rgba(240,200,118,0.45)',
                background: 'rgba(240,200,118,0.15)',
                color: '#f8fafc',
                borderRadius: 10,
                padding: '0.55rem 0.9rem',
                fontWeight: 700,
                fontSize: '0.82rem',
                cursor: 'pointer'
              }}
            >
              Manage Add-Ons
            </button>
          </StateWrap>
        )}

        {hasPropsAccess && loading && (
          <div style={{ display: 'grid', gap: '1.1rem' }}>
            <SkeletonBlock $h="2.6rem" style={{ width: '100%' }} />
            {Array.from({ length: 2 }).map((_, gi) => (
              <div key={gi}>
                <SkeletonBlock $h="1.2rem" style={{ width: '12rem', marginBottom: '0.65rem' }} />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '0.7rem' }}>
                  {Array.from({ length: 5 }).map((_, i) => <SkeletonBlock key={i} $h="5.2rem" />)}
                </div>
              </div>
            ))}
          </div>
        )}

        {hasPropsAccess && !loading && error && (
          <StateWrap>
            <i className="fas fa-exclamation-circle" />
            <h4>Props Unavailable</h4>
            <p>{error}</p>
          </StateWrap>
        )}

        {hasPropsAccess && !loading && !error && !activeGroup && (
          <StateWrap>
            <i className="fas fa-search" />
            <h4>No Props Found</h4>
            <p>{`No ${String(sport).toUpperCase()} player props are available right now. Check back closer to game time.`}</p>
          </StateWrap>
        )}

        {hasPropsAccess && !loading && !error && activeGroup && gameGroups.length === 0 && unmatchedByTeam.size === 0 && (
          <StateWrap>
            <i className="fas fa-search" />
            <h4>No Results</h4>
            <p>No players match your search. Try clearing the search field.</p>
          </StateWrap>
        )}

        {/* Players organized by game → team */}
        {hasPropsAccess && !loading && !error && gameGroups.map(({ game, awayPlayers, homePlayers }) => {
          const awayName = game.awayTeam?.teamName || 'Away';
          const homeName = game.homeTeam?.teamName || 'Home';
          const awayLogo = game.awayTeam?.logoUrl;
          const homeLogo = game.homeTeam?.logoUrl;
          const awayColor = game.awayTeam?.primaryColor;
          const homeColor = game.homeTeam?.primaryColor;
          const gameTime = game.dateTime ? moment(game.dateTime).format('ddd M/D h:mm A') : '';
          return (
            <GameGroupWrap key={game.gameId || `${awayName}-${homeName}`}>
              <GameGroupHeader>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <TeamLogoCircle $color={awayColor} $size="30px">
                    {awayLogo
                      ? <img src={awayLogo} alt={awayName} onError={e => { e.target.style.display = 'none'; }} />
                      : <span style={{ fontSize: '0.55rem', fontWeight: 800, color: awayColor || '#94a3b8' }}>{awayName.slice(0, 3).toUpperCase()}</span>}
                  </TeamLogoCircle>
                  <GameMatchup style={{ color: '#f8fafc' }}>{awayName}</GameMatchup>
                </div>
                <span style={{ color: '#475569', fontSize: '0.85rem', fontWeight: 700, padding: '0 0.35rem' }}>@</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <TeamLogoCircle $color={homeColor} $size="30px">
                    {homeLogo
                      ? <img src={homeLogo} alt={homeName} onError={e => { e.target.style.display = 'none'; }} />
                      : <span style={{ fontSize: '0.55rem', fontWeight: 800, color: homeColor || '#94a3b8' }}>{homeName.slice(0, 3).toUpperCase()}</span>}
                  </TeamLogoCircle>
                  <GameMatchup style={{ color: '#f8fafc' }}>{homeName}</GameMatchup>
                </div>
                {gameTime && <GameTime style={{ marginLeft: 'auto' }}><i className="fas fa-clock" style={{ marginRight: 4 }} />{gameTime}</GameTime>}
              </GameGroupHeader>
              <GameBody>
                {awayPlayers.length > 0 && (
                  <TeamGroupWrap>
                    <TeamGroupHeader>
                      <TeamLogoCircle $color={awayColor} $size="24px">
                        {awayLogo
                          ? <img src={awayLogo} alt={awayName} onError={e => { e.target.style.display = 'none'; }} />
                          : <span style={{ fontSize: '0.48rem', fontWeight: 800, color: awayColor || '#94a3b8' }}>{awayName.slice(0, 3).toUpperCase()}</span>}
                      </TeamLogoCircle>
                      <TeamGroupName>{awayName}</TeamGroupName>
                      <TeamGroupCount>{awayPlayers.length}</TeamGroupCount>
                    </TeamGroupHeader>
                    <TeamGroupGrid>
                      {awayPlayers.map(p => renderPlayerCard(p, game))}
                    </TeamGroupGrid>
                  </TeamGroupWrap>
                )}
                {homePlayers.length > 0 && (
                  <TeamGroupWrap>
                    <TeamGroupHeader>
                      <TeamLogoCircle $color={homeColor} $size="24px">
                        {homeLogo
                          ? <img src={homeLogo} alt={homeName} onError={e => { e.target.style.display = 'none'; }} />
                          : <span style={{ fontSize: '0.48rem', fontWeight: 800, color: homeColor || '#94a3b8' }}>{homeName.slice(0, 3).toUpperCase()}</span>}
                      </TeamLogoCircle>
                      <TeamGroupName>{homeName}</TeamGroupName>
                      <TeamGroupCount>{homePlayers.length}</TeamGroupCount>
                    </TeamGroupHeader>
                    <TeamGroupGrid>
                      {homePlayers.map(p => renderPlayerCard(p, game))}
                    </TeamGroupGrid>
                  </TeamGroupWrap>
                )}
              </GameBody>
            </GameGroupWrap>
          );
        })}

        {/* Unmatched players (no game found) grouped by team */}
        {hasPropsAccess && !loading && !error && unmatchedByTeam.size > 0 && (
          <>
            {Array.from(unmatchedByTeam.entries()).map(([teamName, teamPlayers]) => (
              <TeamGroupWrap key={teamName} style={{ marginBottom: '1rem' }}>
                <TeamGroupHeader>
                  <TeamGroupName>{teamName}</TeamGroupName>
                  <TeamGroupCount>{teamPlayers.length}</TeamGroupCount>
                </TeamGroupHeader>
                <TeamGroupGrid>
                  {teamPlayers.map(p => renderPlayerCard(p, null))}
                </TeamGroupGrid>
              </TeamGroupWrap>
            ))}
          </>
        )}
      </Inner>

      {selectedPlayer && (
        <PlayerModal
          sport={sport}
          player={selectedPlayer}
          onClose={() => setSelectedPlayer(null)}
        />
      )}
    </PageWrap>
  );
};

export default PropsPage;
